import { AccountType } from '@mecontrol/public-api';
import { IPartA, ITelemetryEvent, EventDefinitions, EventParams, PageTags, Flags } from '@mecontrol/common';
import { perfNow, getPageLocation, ME } from '../utilities';
import { scrubUrl } from './utilities';
import { EventMoment, queueEvent, popEvents, captureMoment } from './eventQueue';
import { writeEvent } from './eventWriter';

export type TypedEventCreator<T extends keyof EventDefinitions> =
    (params: EventDefinitions[T]["params"], moment: EventMoment) => EventDefinitions[T]['event'];

type EventCreator = (params: EventParams, moment: EventMoment) => ITelemetryEvent;
type EventCreatorsMap = Record<string, EventCreator>;

// Event generator state
const startTime = perfNow();
const eventCreators: EventCreatorsMap = {};

/**
 * Gets the time that has passed since the MeControl code started running and a
 * passed in moment
 * @param moment Point in time to use measure against start time
 */
export function getTimeSince(moment: number): number {
    return moment - startTime;
}

/**
 * Builds the PageTags to send with an event
 * @param accounts Information on the current accounts template
 * @param accountType Type of the currently signed in Account
 * @returns PageTags object to send in an event
 */
export function getPageTags(accounts: string, accountType?: AccountType): PageTags {
    // Scrub all query parameters
    const url = scrubUrl(getPageLocation().href);

    return {
        metaTags: {
            authtype: accountType && accountType.toString(),
            pgpart: ME.Config.ptn
        },
        config: ME.Config,
        url,
        accts: accounts
    };
}

/**
 * Creates an object containing the part A data for a telemetry event
 * @param name The type of the event to create a Part A for
 * @param eventDate Date when the event took place
 * @param cv Correlation Vector string for the event
 */
export function createPartA(name: string, eventDate: Date, eventCv: string): IPartA {
    return {
        ver: '2.1',
        name,
        time: eventDate.toISOString(), // IE 9+
        appId: 'JS:MeControl',
        cV: eventCv,
        flags: Flags.DropIdentifiers,
        ext: {
            javascript: {
                ver: '1.1',
                libVer: '4.2.3',
                domain: getPageLocation().hostname,
                userConsent: false
            },
            app: {
                env: ME.Config.int ? 'Test' : 'Prod'
            }
        }
    };
}

/**
 * Sets the function that will handle creation of a certain type of Telemetry Event
 * @param type A string containing the type of event we are registering a creation function for
 * @param eventCreator A function that produces events of type 'type'.
 * Takes in the type's parameter object
 */
export function setEventCreator<T extends keyof EventDefinitions>(type: T, eventCreator: TypedEventCreator<T>): void {
    eventCreators[type] = eventCreator;

    // Process any events of this type that may be queued
    let queuedEvents = popEvents(type);
    queuedEvents.forEach(eventData => {
        let event = eventCreator(eventData[0], eventData[1]);
        writeEvent(event, eventData[0].isCritical);
    });
}

/**
 * Creates an event of a given type.
 * @param eventParams An object that contains all the information needed to create the event and that
 * will be passed to the creator function
 * @returns The created event or NULL if no creator for the type of event is found
 */
export function createEvent(eventParams: EventParams): ITelemetryEvent | null {
    let eventCreator = eventCreators[eventParams.eventType];
    if (eventCreator) {
        return eventCreator(eventParams, captureMoment(eventParams));
    }

    return null;
}

/**
 * Create and send an event
 * @param eventParams An object that contains all the information needed to create the event and that
 * will be passed to the creator function
 */
export function fireEvent(eventParams: EventParams): void {
    let event = createEvent(eventParams);
    if (event) {
        writeEvent(event, eventParams.isCritical);
    }
    else {
        queueEvent(eventParams);
    }
}
